import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import { ButtonShapes } from '@mlb/react-patterns/v4/components/Button/button.types';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';

import useTracking from '@gameday/utils/hooks/useTracking';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';

import { TicketWrapper, TicketButton } from '../Preview/Preview.style';

const TFL_PARAM_START = 'tfl=';
const QUESTION_MARK = '?';
const AMPERSAND = '&';

const getIsLink = (str) => {
    return str.startsWith('http://') || str.startsWith('https://');
};

const BuyTicketsButton = ({
    ticketLink,
    isLoading,
    disableTicketLink,
    abstractGameState
}) => {
    const translate = useTranslate();
    const ticketLabel = translate('tickets_buy', 'Buy Tickets');
    const Tracking = useTracking();

    const onClick = useCallback((event) => {
        const text = event?.currentTarget.innerText ?? '';
        const activity = `${text} Click`;
        const trackingCfg = {
            componentName: [abstractGameState],
            activity,
            text,
            linkName: activity
        };
        Tracking.track.track_click(trackingCfg);
    }, [Tracking, abstractGameState]);
    const handlers = useMemo(() => ({ onClick }), [onClick]);

    // The ticketing url modified by makeTFL if present
    const composedTicketingUrl = useMemo(() => {
        if (typeof window !== 'undefined' && ticketLink) {
            // Get the makeTFL function from the window object if it exists
            const makeTFL = window?.makeTFL;
            const makeTFLFunction = makeTFL?.default || makeTFL;

            // If makeTFLFunction is not defined, return the original ticketLink
            if (!makeTFLFunction) {
                return ticketLink;
            }

            // Generate the TFL string using the makeTFL function
            const tflString = makeTFL({
                url: ticketLink,
                component: 'Gameday'
            });

            // If the generated TFL string is a valid link, return it
            if (getIsLink(tflString)) {
                return tflString;
            }

            // Otherwise, construct the ticket link url by appending the TFL parameters to the original ticketLink
            const tflStart = `${tflString.indexOf(TFL_PARAM_START) === -1 ? TFL_PARAM_START : ''}`;
            const paramAddition = `${ticketLink.indexOf(QUESTION_MARK) === -1 ? QUESTION_MARK : AMPERSAND}`;
            return `${ticketLink}${paramAddition}${tflStart}${tflString}`;
        }

        return ticketLink;
    }, [ticketLink]);

    return !disableTicketLink && ticketLink
        ? (
            <TicketWrapper>
                {
                    isLoading
                        ? <Skeleton width='232px' height='48px' margin='0' />
                        : (
                            <Link
                                url={composedTicketingUrl}
                                target='_blank'
                                ariaLabel={ticketLabel}
                                handlers={handlers}
                            >
                                <TicketButton
                                    shape={ButtonShapes.PILL_SOLID}
                                    label={ticketLabel}
                                />
                            </Link>
                        )
                }
            </TicketWrapper>
        )
        : null;
};

BuyTicketsButton.propTypes = {
    ticketLink: PropTypes.string,
    isLoading: PropTypes.bool,
    disableTicketLink: PropTypes.bool,
    abstractGameState: PropTypes.string
};

export default BuyTicketsButton;
